import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TwitterIcon from '@mui/icons-material/Twitter';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { ROUTES, URN_QDOS_S3, URI_QDOS } from '../../helpers/constans';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { thingQdosActions } from '../../redux/slices/thing-qdos';
import { styles } from './things-qdos-media.styles';
import { shareActions } from '../../redux/slices/share.slice';
import { notificationActions } from '../../redux/slices/notification.slice';

const FALLBACK_QDOS_PUBLIC_BASE_URL = 'https://admin.qdos.bz';

const AddChipButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}): ReactElement => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: '#1976d2',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>

      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '0.9rem',
          color: '#1f1f1f',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const ThingsQdosMedia = (): ReactElement => {
  const { posts } = useSelector((state: RootState) => state.thingsQdos);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: itemId } = useParams();

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [portraitMap, setPortraitMap] = useState<Record<string, boolean>>({});
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!itemId) return;
    dispatch(thingQdosActions.getAllPostsByItemId(itemId));
  }, [dispatch, itemId]);

  const isCurrentThing = true;
  const qdosEventId = posts?.[0]?.post?.eventId;

  const selectedPost = useMemo(() => selectedItem?.post || {}, [selectedItem]);

  const handleBack = (): void => {
    navigate(ROUTES.things);
  };

  const getMediaUrl = (value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value}`;
  };

  const getImageKey = (entry: any): string => {
    return entry?._id || entry?.post?._id || entry?.post?.imageId || 'unknown-image';
  };

  const getQdosPublicBaseUrl = (): string => {
    const normalizedUriQdos = (URI_QDOS || '').trim().replace(/\/+$/, '');

    if (normalizedUriQdos.includes('admin.qdos.bz')) {
      return normalizedUriQdos;
    }

    return FALLBACK_QDOS_PUBLIC_BASE_URL;
  };

  const getQdosPublicUrl = (entry: any): string => {
    const alternativeId = entry?.post?.alternativeId;

    if (!alternativeId) return '';
    return `${getQdosPublicBaseUrl()}/posts/${alternativeId}`;
  };

  const getDownloadUrl = (entry: any): string => {
    const post = entry?.post || {};
    return getMediaUrl(post?.videoUrl || post?.imageId);
  };

  const getDownloadFileName = (entry: any): string => {
    const post = entry?.post || {};
    const alternativeId = post?.alternativeId || 'qdos-media';
    const mediaUrl = getDownloadUrl(entry);

    if (mediaUrl.includes('.mp4')) return `${alternativeId}.mp4`;
    if (mediaUrl.includes('.mov')) return `${alternativeId}.mov`;
    if (mediaUrl.includes('.webm')) return `${alternativeId}.webm`;
    if (mediaUrl.includes('.png')) return `${alternativeId}.png`;
    if (mediaUrl.includes('.jpeg') || mediaUrl.includes('.jpg')) return `${alternativeId}.jpg`;

    return post?.videoUrl ? `${alternativeId}.mp4` : `${alternativeId}.jpg`;
  };

  const handleImageLoad =
    (key: string) =>
    (event: React.SyntheticEvent<HTMLImageElement>): void => {
      const img = event.currentTarget;
      const isPortrait = img.naturalHeight > img.naturalWidth;

      setPortraitMap((prev) => {
        if (prev[key] === isPortrait) return prev;
        return { ...prev, [key]: isPortrait };
      });
    };

  const handleAddJoy = (entry: any): void => {
    const post = entry?.post || {};

    const qdosItemId = post?.itemIds?.[0];
    const eventId = post?.eventId;
    const qdosPostId = post?._id;

    if (!qdosItemId || !eventId || !qdosPostId) {
      console.log('JOY CLICK MISSING DATA', {
        qdosItemId,
        eventId,
        qdosPostId,
        entry,
        post,
      });
      return;
    }

    dispatch(
      shareActions.addMemorableMoment({
        qdosItemId,
        qdosEventId: eventId,
        qdosPostId,
        alternativeId: post?.alternativeId,
        mediaType: post?.mediaType,
        imageId: post?.imageId,
        videoUrl: post?.videoUrl,
        description: post?.description,
        eventTitle: post?.eventTitle,
      }),
    );
  };

  const handleAddToShare = (
    entry: any,
    shareType?: 'team' | 'fan',
  ): void => {
    const post = entry?.post || {};

    const qdosItemId = post?.itemIds?.[0];
    const eventId = post?.eventId;
    const qdosPostId = post?._id;

    if (!qdosItemId || !eventId || !qdosPostId || !shareType) {
      console.log('SHARE CLICK MISSING DATA', {
        qdosItemId,
        eventId,
        qdosPostId,
        shareType,
        entry,
        post,
      });
      return;
    }

    dispatch(
      shareActions.addToShare({
        qdosItemId,
        qdosEventId: eventId,
        qdosPostId,
        shareType,
        alternativeId: post?.alternativeId,
        mediaType: post?.mediaType,
        imageId: post?.imageId,
        videoUrl: post?.videoUrl,
        description: post?.description,
        eventTitle: post?.eventTitle,
      }),
    );
  };

  const handleViewTeamShare = (): void => {
    if (!qdosEventId) return;
    navigate(generatePath(ROUTES.teamShare, { eventId: qdosEventId }));
  };

  const handleViewFanShare = (): void => {
    if (!qdosEventId) return;
    navigate(generatePath(ROUTES.fanShare, { eventId: qdosEventId }));
  };

  const handleOpenShareDialog = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = (): void => {
    setShareDialogOpen(false);
  };

  const handleCopyLink = async (): Promise<void> => {
    const url = getQdosPublicUrl(selectedItem);

    if (!url) {
      dispatch({
        type: notificationActions.error.type,
        payload: 'No public QDOS link is available for this media',
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      dispatch({
        type: notificationActions.success.type,
        payload: 'Public QDOS link copied to clipboard',
      });
      setShareDialogOpen(false);
    } catch (error) {
      dispatch({
        type: notificationActions.error.type,
        payload: 'Unable to copy link to clipboard',
      });
    }
  };

  const handleWhatsAppShare = (): void => {
    const url = getQdosPublicUrl(selectedItem);

    if (!url) {
      dispatch({
        type: notificationActions.error.type,
        payload: 'No public QDOS link is available for this media',
      });
      return;
    }

    const shareText = encodeURIComponent(url);
    window.open(`https://wa.me/?text=${shareText}`, '_blank', 'noopener,noreferrer');
  };

  const handleTwitterShare = (): void => {
    const url = getQdosPublicUrl(selectedItem);

    if (!url) {
      dispatch({
        type: notificationActions.error.type,
        payload: 'No public QDOS link is available for this media',
      });
      return;
    }

    const text = encodeURIComponent('Check this out on QDOS');
    const encodedUrl = encodeURIComponent(url);

    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleDownload = async (): Promise<void> => {
    const mediaUrl = getDownloadUrl(selectedItem);

    if (!mediaUrl) {
      dispatch({
        type: notificationActions.error.type,
        payload: 'No media file available to download',
      });
      return;
    }

    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = blobUrl;
      anchor.download = getDownloadFileName(selectedItem);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);

      setShareDialogOpen(false);
    } catch (error) {
      window.open(mediaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderMedia = (entry: any, enlarged = false): ReactElement => {
    const post = entry?.post || {};
    const imageUrl = getMediaUrl(post?.imageId);
    const videoUrl = getMediaUrl(post?.videoUrl);
    const imageKey = getImageKey(entry);
    const isPortrait = portraitMap[imageKey];

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '75vh' : '260px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box
          component="img"
          src={imageUrl}
          alt={post?.eventTitle || 'QDOS media'}
          onLoad={handleImageLoad(imageKey)}
          sx={{
            width: '100%',
            height: enlarged ? 'auto' : '220px',
            maxHeight: enlarged ? '75vh' : '220px',
            objectFit: enlarged ? 'contain' : isPortrait ? 'contain' : 'cover',
            borderRadius: '8px',
            display: 'block',
            backgroundColor: '#f5f5f5',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: enlarged ? '260px' : '220px',
          borderRadius: '8px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '1rem',
          color: '#777',
          textAlign: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography>{post?.alternativeId || 'No media preview available'}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={styles.container}>
      {!isCurrentThing ? (
        <Box sx={{ mt: '2rem' }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <Box sx={styles.mainBox}>
          <Box sx={styles.mainTitleBox}>
            <Box>
              <Typography sx={styles.mainTitle}>QDOS Media</Typography>
              <Typography sx={styles.mainSubTitle}>Posts from QDOS</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button sx={styles.backButton} onClick={handleBack}>
                back
              </Button>
              <Button sx={styles.backButton} onClick={handleViewTeamShare}>
                View Team Share
              </Button>
              <Button sx={styles.backButton} onClick={handleViewFanShare}>
                View Fan Share
              </Button>
            </Box>
          </Box>

          <Box sx={styles.mainContentBox}>
            {[...(posts || [])]
              .slice()
              .reverse()
              .map((el: any) => {
                const post = el?.post || {};
                return (
                  <Box
                    key={el._id}
                    onClick={() => setSelectedItem(el)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      p: '1rem',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                    }}
                  >
                    {renderMedia(el)}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {post?.eventTitle || 'Untitled event'}
                      </Typography>

                      {post?.description && (
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {post.description}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <AddChipButton
                        label="Joy"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddJoy(el);
                        }}
                      />

                      <AddChipButton
                        label="Team"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToShare(el, 'team');
                        }}
                      />

                      <AddChipButton
                        label="Fan"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToShare(el, 'fan');
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => {
          setSelectedItem(null);
          setShareDialogOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 1,
          }}
        >
          <Typography variant="h6">
            {selectedPost?.eventTitle || 'QDOS media'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <IconButton onClick={handleOpenShareDialog}>
              <ShareIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setSelectedItem(null);
                setShareDialogOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: '0.5rem 0 1rem 0' }}>{renderMedia(selectedItem, true)}</Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            {selectedPost?.description && (
              <Typography variant="body1">{selectedPost.description}</Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: '0.75rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <AddChipButton
                label="Joy"
                onClick={(event) => {
                  event.stopPropagation();
                  handleAddJoy(selectedItem);
                }}
              />
              <AddChipButton
                label="Team"
                onClick={(event) => {
                  event.stopPropagation();
                  handleAddToShare(selectedItem, 'team');
                }}
              />
              <AddChipButton
                label="Fan"
                onClick={(event) => {
                  event.stopPropagation();
                  handleAddToShare(selectedItem, 'fan');
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog}>
        <DialogContent
          sx={{
            minWidth: { xs: '280px', sm: '360px' },
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            p: '1rem',
          }}
        >
          <Typography variant="h6">Share media</Typography>

          <Button
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyLink}
            sx={{ justifyContent: 'flex-start' }}
          >
            Copy public link
          </Button>

          <Button
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsAppShare}
            sx={{ justifyContent: 'flex-start' }}
          >
            Share on WhatsApp
          </Button>

          <Button
            startIcon={<TwitterIcon />}
            onClick={handleTwitterShare}
            sx={{ justifyContent: 'flex-start' }}
          >
            Share on X
          </Button>

          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ justifyContent: 'flex-start' }}
          >
            Download media
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ThingsQdosMedia;